import h from 'hyperscript';

import { readDataProtection } from 'cadenza/api-client/data-protection-api';
import { getObjecttypeDataProtectionContext } from 'cadenza/api-client/data-protection-context-api';
import type { DataProtectionDescriptionDto } from 'cadenza/api-client/dto/data-protection-dto';
import type { DataProtectionMode } from 'cadenza/api-client/dto/data-protection-mode';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import type { WorkbookMetadataDto } from 'cadenza/api-client/dto/workbook-metadata-dto';
import { DataProtectionAssignmentPanel } from 'cadenza/data-protection-context/data-protection-assignment-panel';
import { isFeatureAvailable } from 'cadenza/features';
import type { DataProtectionContextId } from 'cadenza/repository/model/data-protection-context-id.gen';
import type { DataProtectionContextName } from 'cadenza/repository/model/data-protection-context-name.gen';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import { objecttypeIdFromString } from 'cadenza/repository/model/objecttype-id.gen';
import { repositoryService } from 'cadenza/repository/repository-service';

export function buildDataProtectionAssignmentPickerForWorkbook(
  metadata: WorkbookMetadataDto,
  repository: RepositoryDto,
  repositoryDpcIds: DataProtectionContextId[],
) {
  if (repository.dataProtectionMode === 'contexts') {
    // The component is visible if repository security mode 'Use data contexts'
    // AND relevant for workbooks (workbook dpc is enabled or there is at least 1 bound OT without DPC or with DPC which does not exist)
    const repositoryDpcNames = repositoryDpcIds.map((dpcId) => dpcId.itemName);
    const dpcsNotInRepository =
      metadata.referencedDataProtectionContextNames.filter((dpcName) => !repositoryDpcNames?.includes(dpcName)).length >
      0;
    if (repository.dpcWorkbooksEnabled || metadata.hasBoundObjecttypesWithoutDPC || dpcsNotInRepository) {
      // the first DPC that is in both the importing workbook metadata and in the selected repository is selected in the picker
      const referencedDpcNames = getDataProtectionNames(metadata);

      const dpcNamesIntersection = referencedDpcNames.filter((dpcName) => repositoryDpcNames?.includes(dpcName));
      const dpcName = dpcNamesIntersection.length > 0 ? dpcNamesIntersection[0] : undefined;
      const presetDataProtectionContextId =
        dpcName ? repositoryDpcIds?.find((dpcId) => dpcName === dpcId.itemName) : undefined;
      return buildDataProtectionAssignmentPickerIfNeeded(repository, presetDataProtectionContextId);
    }
  }
}

export function buildDataProtectionAssignmentPickerIfNeeded(
  repository?: RepositoryDto,
  presetDataProtectionContextId?: DataProtectionContextId,
) {
  if (isFeatureAvailable('DATA_PROTECTION')) {
    const dataProtectionMode = repository?.dataProtectionMode;
    if (dataProtectionMode && dataProtectionMode === 'contexts') {
      return new DataProtectionAssignmentPanel(repository, {
        presetDataProtectionContextId,
        pickerOnly: true,
      });
    }
  }
}

function buildDataProtectionAssignmentPanelIfNeeded(
  repository?: RepositoryDto,
  presetDataProtectionContextId?: DataProtectionContextId,
  presetDataProtectionDescription?: DataProtectionDescriptionDto,
) {
  const dataProtectionMode = repository?.dataProtectionMode;
  if (dataProtectionMode && dataProtectionMode !== 'none') {
    return new DataProtectionAssignmentPanel(repository, {
      presetDataProtectionContextId,
      presetDataProtectionDescription,
    });
  }
}

export async function handleDPCAssignmentDisplay(
  parent: HTMLElement,
  repository: RepositoryDto,
  dpcAssignmentPanel?: DataProtectionAssignmentPanel,
  presetDataProtectionContextId?: DataProtectionContextId,
  presetDataProtectionDescription?: DataProtectionDescriptionDto,
) {
  if (isFeatureAvailable('DATA_PROTECTION')) {
    dpcAssignmentPanel?.remove();
    const dataProtectionMode = repository.dataProtectionMode;
    if (!dataProtectionMode || dataProtectionMode === 'none') {
      return undefined;
    }
    dpcAssignmentPanel = buildDataProtectionAssignmentPanelIfNeeded(
      repository,
      presetDataProtectionContextId,
      presetDataProtectionDescription,
    );
    if (dpcAssignmentPanel) {
      parent.append(dpcAssignmentPanel);
    }
    return dpcAssignmentPanel;
  }
}

export async function handleDPCAssignmentWithPresetsDisplay(
  objecttypeId: ObjecttypeIdString,
  parent: HTMLElement,
  dpcAssignmentPanel?: DataProtectionAssignmentPanel,
) {
  if (isFeatureAvailable('DATA_PROTECTION')) {
    dpcAssignmentPanel?.remove();
    const repositoryName = objecttypeIdFromString(objecttypeId).repositoryName;
    const repository = await repositoryService().get(repositoryName);
    const dataProtectionMode = repository.dataProtectionMode;
    if (!dataProtectionMode || dataProtectionMode === 'none') {
      return undefined;
    }
    const [objecttypeDpc, objecttypeDpDesc] = await Promise.all([
      dataProtectionMode === 'contexts' ? getObjecttypeDataProtectionContext(objecttypeId) : undefined,
      readDataProtection(objecttypeId),
    ]);
    dpcAssignmentPanel = buildDataProtectionAssignmentPanelIfNeeded(
      repository,
      objecttypeDpc?.itemId,
      objecttypeDpDesc,
    );
    if (dpcAssignmentPanel) {
      parent.append(dpcAssignmentPanel);
    }
    return dpcAssignmentPanel;
  }
}

export function createReadOnlyDataProtectionColumnElement(
  shortName: string,
  labelTitle: string,
  dataValidationHint?: string,
) {
  const formGroup = h('form-group', [h('label', labelTitle), shortName]);
  if (dataValidationHint != null) {
    formGroup.dataset.validationHint = dataValidationHint;
  }
  return formGroup;
}

export function getDataProtectionMode(workbookMetadata: WorkbookMetadataDto): DataProtectionMode {
  if (getDataProtectionNames(workbookMetadata).length) {
    return 'contexts';
  }
  if (workbookMetadata && workbookMetadata.hasBoundObjecttypesWithDclOrDpls) {
    return 'labeled';
  }
  return 'none';
}

function getDataProtectionNames(workbookMetadata: WorkbookMetadataDto) {
  if (!workbookMetadata) {
    return [];
  }
  const referencedDpcNames: DataProtectionContextName[] = [];
  if (workbookMetadata.workbookDataProtectionContextName) {
    referencedDpcNames.push(workbookMetadata.workbookDataProtectionContextName);
  }
  referencedDpcNames.push(...workbookMetadata.referencedDataProtectionContextNames);
  return referencedDpcNames;
}
